import { isCustomerPickup } from './purchaseOrderRoute';

const text = (value) => String(value ?? '').trim();
const numericId = (value) => /^\d+$/.test(text(value));

const optionLabel = (option) => text(
  option?.name ?? option?.label ?? option?.shippingMethodName ?? option?.value
);

const optionId = (option) => text(
  option?.id ?? option?.shippingMethodId ?? option?.value
);

const humanizeShippingMethod = (value) => {
  const raw = text(value);
  if (!raw) return '';
  const aliases = {
    GROUND_BEST: 'Ground - Best Way',
    'GROUND - BEST WAY': 'Ground - Best Way',
    'GROUND BEST WAY': 'Ground - Best Way',
    'BEST WAY': 'Ground - Best Way',
    CUSTOMER_PICKUP: 'Customer Pickup',
    'CUSTOMER PICKUP': 'Customer Pickup',
    'CUSTOMER PICK UP': 'Customer Pickup',
    PICKUP: 'Customer Pickup',
  };
  return aliases[raw.toUpperCase()] || raw;
};

export const resolveGeneratedPoShippingMethodLabel = ({ labels = [], values = [], options = [] } = {}) => {
  const explicitLabel = labels.map(text).find((candidate) => candidate && !numericId(candidate));
  if (explicitLabel) return humanizeShippingMethod(explicitLabel);

  const rawValue = values.map(text).find(Boolean);
  if (!rawValue) return '—';
  const matchingOption = (Array.isArray(options) ? options : [])
    .find((option) => optionId(option) === rawValue);
  const resolvedOptionLabel = optionLabel(matchingOption);
  if (resolvedOptionLabel) return humanizeShippingMethod(resolvedOptionLabel);

  return numericId(rawValue) ? '—' : humanizeShippingMethod(rawValue);
};

export const resolvePurchaseOrderShippingMethodLabel = ({ purchaseOrder = {}, supplierInfo = {}, options } = {}) => {
  const supplierMethod = text(supplierInfo?.shippingMethod);
  const purchaseOrderMethod = text(purchaseOrder?.shippingMethod);
  const readableMethods = [supplierMethod, purchaseOrderMethod].filter((value) => value && !numericId(value));
  const numericMethods = [supplierMethod, purchaseOrderMethod].filter((value) => value && numericId(value));
  const optionSources = [
    options,
    purchaseOrder?.shippingMethodOptions,
    purchaseOrder?.shipMethods,
    supplierInfo?.shippingMethodOptions,
    supplierInfo?.shipMethods,
  ].filter(Array.isArray);

  return resolveGeneratedPoShippingMethodLabel({
    labels: [
      supplierInfo?.shippingMethodLabel,
      purchaseOrder?.shippingMethodLabel,
    ],
    values: [
      ...readableMethods,
      supplierInfo?.shippingMethodId,
      purchaseOrder?.shippingMethodId,
      ...numericMethods,
    ],
    options: optionSources.find((source) => source.length) || [],
  });
};

export const buildGeneratedPoShippingHeadline = ({ route, routeDisplay, methodLabel } = {}) => {
  const explicitRouteLabel = text(routeDisplay?.label);
  const routeLabel = explicitRouteLabel || (
    ['decorator_to_client', 'supplier_to_decorator'].includes(route?.legType) &&
    text(route?.origin?.name) && text(route?.destination?.name)
      ? `${text(route.origin.name)} → ${text(route.destination.name)}`
      : ''
  );
  if (!routeLabel) return '';
  if (!isCustomerPickup(route?.fulfillment?.method) && !isCustomerPickup(methodLabel)) return routeLabel;
  return /\(customer\s+pick\s*up\)\s*$/i.test(routeLabel)
    ? routeLabel
    : `${routeLabel} (Customer Pickup)`;
};

// Only the supplier's receiving leg uses the decorator recipient snapshot.
// The decorator's onward/customer destination keeps its own contact and address.
export const resolveGeneratedPoRecipientName = ({ route, address, methodLabel } = {}) => {
  if (route?.legType !== 'supplier_to_decorator' ||
      isCustomerPickup(route?.fulfillment?.method) || isCustomerPickup(methodLabel)) return '';
  return text(address?.recipientName);
};

export const removePhysicalLocationHeadline = (lines = [], address = {}) => {
  if (!address || typeof address !== 'object') return lines;
  const physicalNames = new Set([
    address.name, address.company, address.companyName, address.contactName,
  ].map((value) => text(value).toLowerCase()).filter(Boolean));
  return lines.filter((line) => !physicalNames.has(text(line).toLowerCase()));
};

